import { useState, useEffect } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import axios from 'axios';
import { Plus, Pencil, Trash2, History, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { AppointmentHistoryDrawer } from '@/components/appointments/AppointmentHistoryDrawer';
import { isValidBusinessEmail, EMAIL_ERROR_TR } from '@/lib/email';
import { useSectorTerms } from '@/utils/useSectorTerms';

export const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', apartment_number: '', phone: '' });
  const [historyUserId, setHistoryUserId] = useState(null);
  const { user: currentUser } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;
  const isStaff = currentUser?.role === 'staff';
  const terms = useSectorTerms();
  // Show appointment history button only for appointment-style businesses
  const isAppointmentBiz = (
    currentUser?.appointment_category ||
    ['barber', 'beauty', 'dental'].includes(currentUser?.business_type)
  );

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      setUsers(data);
    } catch (error) {
      toast.error('Kullanıcılar yüklenemedi');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Only validate email on CREATE; on edit, the field may be untouched and we
    // do not want to lock an admin out of editing a customer with a legacy email.
    if (!editingUser && formData.email && !isValidBusinessEmail(formData.email)) {
      toast.error(EMAIL_ERROR_TR);
      return;
    }
    try {
      if (editingUser) {
        await axios.put(`${API_URL}/api/users/${editingUser.id}`, formData, { withCredentials: true });
        toast.success('Kullanıcı güncellendi');
      } else {
        await axios.post(`${API_URL}/api/users`, formData, { withCredentials: true });
        toast.success('Kullanıcı oluşturuldu');
      }
      setDialogOpen(false);
      setFormData({ name: '', email: '', password: '', apartment_number: '', phone: '' });
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Kullanıcıyı silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/api/users/${userId}`, { withCredentials: true });
      toast.success('Kullanıcı silindi');
      fetchUsers();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({ name: user.name, email: user.email, password: '', apartment_number: user.apartment_number || '', phone: user.phone || '' });
    setDialogOpen(true);
  };

  return (
    <PageLayout>
      
      
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]" data-testid="users-page-title">
                {terms.customerLabelPlural}
              </h1>
              <p className="text-base text-[#595959] mt-2">{terms.customerLabelPlural} yönetimi</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              {!isStaff && (
                <DialogTrigger asChild>
                  <Button
                    className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white"
                    onClick={() => {
                      setEditingUser(null);
                      setFormData({ name: '', email: '', password: '', apartment_number: '', phone: '' });
                    }}
                    data-testid="add-user-button"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    {terms.addCustomerLabel}
                  </Button>
                </DialogTrigger>
              )}
              <DialogContent data-testid="user-dialog">
                <DialogHeader>
                  <DialogTitle>{editingUser ? `${terms.customerLabelSingular} Düzenle` : `Yeni ${terms.customerLabelSingular}`}</DialogTitle>
                  <DialogDescription>{terms.customerLabelSingular} bilgilerini girin</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Ad Soyad</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="user-name-input"
                    />
                  </div>
                  <div>
                    <Label>E-posta</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required={!editingUser}
                      disabled={!!editingUser}
                      data-testid="user-email-input"
                    />
                  </div>
                  <div>
                    <Label>Şifre {editingUser && '(Boş bırakılabilir)'}</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={!editingUser}
                      data-testid="user-password-input"
                    />
                  </div>
                  <div>
                    <Label>{terms.customerNoLabel}</Label>
                    <Input
                      value={formData.apartment_number}
                      onChange={(e) => setFormData({ ...formData, apartment_number: e.target.value })}
                      data-testid="user-apartment-input"
                      placeholder={`örn: ${terms.customerNoLabel}`}
                    />
                  </div>
                  <div>
                    <Label>Telefon</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      data-testid="user-phone-input"
                      placeholder="05XX XXX XX XX"
                    />
                  </div>
                  <Button type="submit" className="w-full" data-testid="user-submit-button">
                    {editingUser ? 'Güncelle' : 'Oluştur'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white border border-[#E5E5E5] rounded-xl hidden md:block" data-testid="users-table-container">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ad Soyad</TableHead>
                  <TableHead>E-posta</TableHead>
                  <TableHead>{terms.customerNoLabel}</TableHead>
                  <TableHead>Telefon</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.apartment_number || '-'}</TableCell>
                    <TableCell>{user.phone || '-'}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs ${
                        user.role === 'admin' ? 'bg-[#2563EB] text-white' : 'bg-[#F9F8F6] text-[#595959]'
                      }`}>
                        {user.role}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {user.role !== 'admin' && (
                        <div className="flex justify-end gap-2">
                          {isAppointmentBiz && (
                            <Button variant="ghost" size="sm" onClick={() => setHistoryUserId(user.id)} title="Randevu Geçmişi" data-testid={`history-user-${user.id}`}>
                              <History className="w-4 h-4 text-[#2563EB]" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(user)} data-testid={`edit-user-${user.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(user.id)} data-testid={`delete-user-${user.id}`}>
                            <Trash2 className="w-4 h-4 text-[#DC2626]" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3" data-testid="users-mobile-cards">
            {users.map((user) => (
              <div key={user.id} className="bg-white border border-[#E5E5E5] rounded-xl p-4" data-testid={`user-card-${user.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">{user.name}</p>
                    <p className="text-sm text-[#595959]">{user.email}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${user.role === 'admin' ? 'bg-[#2563EB] text-white' : 'bg-[#F9F8F6] text-[#595959]'}`}>{user.role}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#595959] mb-2">
                  {user.apartment_number && <span>{terms.customerNoLabel}: {user.apartment_number}</span>}
                  {user.phone && <span>Tel: {user.phone}</span>}
                </div>
                {user.role !== 'admin' && (
                  <div className="flex gap-2 pt-2 border-t border-[#E5E5E5]">
                    {isAppointmentBiz && (
                      <Button variant="outline" size="sm" onClick={() => setHistoryUserId(user.id)} className="text-xs h-9" data-testid={`m-history-${user.id}`}>
                        <History className="w-3.5 h-3.5 mr-1" /> Geçmiş
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => openEditDialog(user)} className="flex-1 text-xs h-9">
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Düzenle
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(user.id)} className="text-xs h-9 text-red-500 border-red-200 hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Sil
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ===== Appointment History Drawer ===== */}
        <AppointmentHistoryDrawer
          userId={historyUserId}
          open={!!historyUserId}
          onClose={() => setHistoryUserId(null)}
        />
      
    </PageLayout>
  );
};
